import { ReactNode } from "react";
import type { CalendarProvider } from "@plura/shared/types";

export interface PricingFeature {
  id: string;
  title: string | ReactNode;
  description?: string;
  icon?: string;
  schedulerProviders?: CalendarProvider[];
}

export interface PricingCard {
  title: string;
  description: string;
  icon: string;
  price: {
    monthly: number;
    yearly: number;
  }
  features: {
    card: PricingFeature[];
    usage: PricingFeature[];
    core: PricingFeature[];
    branding: PricingFeature[];
    customizations: PricingFeature[];
    insight: PricingFeature[];
    support: PricingFeature[];
    sharing: PricingFeature[];
  }
}

export const plans: PricingCard[] = [
  {
    title: 'Free',
    description: 'Unlimited forms and responses',
    icon: 'fa-regular fa-cube',
    price: {
      monthly: 0,
      yearly: 0,
    },
    features: {
      card: [
        {
          id: 'unlimited-forms',
          title: 'Unlimited forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'unlimited-responses',
          title: 'Unlimited responses',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'unlimited-questions',
          title: 'Unlimited questions per form',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'template-forms',
          title: 'Template forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'add-logic',
          title: 'Add logic to forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'share-forms',
          title: 'Embed, popup & banner forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'ai-assistant',
          title: 'AI Chat Assistant (10 messages)',
          icon: 'fa-regular fa-circle-check',
        },
      ],
      usage: [
        {
          id: 'number-of-forms',
          title: 'Number of forms',
          description: 'Unlimited',
        },
        {
          id: 'questions-per-form',
          title: 'Questions per form',
          description: 'Unlimited',
        },
        {
          id: 'form-responses',
          title: 'Form responses',
          description: 'Unlimited',
        },
        {
          id: 'workspace-members',
          title: 'Workspace members',
          description: '1',
        },
      ],
      core: [],
      branding: [],
      customizations: [
        {
          id: 'partial-submissions',
          title: 'Partial submissions',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'duplicate-form',
          title: 'Duplicate forms',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'custom-domains',
          title: 'Custom domains',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'ai-form-builder',
          title: 'AI Chat Assistant',
          description: '10 messages',
        },
        {
          id: 'ai-support',
          title: 'AI Support & Guidance',
          description: '10 messages',
        },
        {
          id: 'ai-conversation',
          title: 'AI conversation blocks',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'upload-media',
          title: 'Upload your own media',
          description: '10MB limit',
        },
        {
          id: 'notification-emails',
          title: 'Notification emails on form submissions',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'remove-branding',
          title: 'Remove built with Formsuite badge',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'branding-logo',
          title: 'Branding logo',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'advanced-ai-training',
          title: 'Advanced AI training',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'add-logic-to-forms',
          title: 'Add logic to forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'hidden-fields',
          title: 'Hidden fields',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'template-forms',
          title: 'Template forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-fonts',
          title: 'Custom fonts',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-themes',
          title: 'Custom themes',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'scheduler-bookings',
          title: 'Scheduler bookings',
          schedulerProviders: ['CALENDLY'],
        },
        {
          id: 'google-calendar-scheduling',
          title: 'Google Calendar & Meet links',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'revisit-incomplete',
          title: 'Revisit incomplete forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'multi-question-pages',
          title: 'Multi-question pages',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'webhooks',
          title: 'Webhooks',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'redirect-question',
          title: 'Redirect question',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'form-folders',
          title: 'Form folders',
          description: '2',
        },
        {
          id: 'custom-themes-count',
          title: 'Saved custom themes',
          description: '1',
        },
      ],
      insight: [
        {
          id: 'ai-lead-analysis',
          title: 'AI lead analysis',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'lead-summaries',
          title: 'Response summaries',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'reports',
          title: 'Reports',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'download-responses',
          title: 'Download responses',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'advanced-form-metrics',
          title: 'Advanced form metrics',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'workspace-metrics',
          title: 'Workspace metrics',
          icon: 'fa-regular fa-circle-check',
        },
      ],
      support: [],
      sharing: [
        {
          id: 'customized-end-screen',
          title: 'Customized end screen',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'link-to-url',
          title: 'Link to URL on end screen',
          icon: 'fa-regular fa-circle-x',
        },
        {
          id: 'embed-forms',
          title: 'Embed forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'popup-forms',
          title: 'Popup forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'banner-forms',
          title: 'Banner forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-domains-count',
          title: 'Custom domains',
          icon: 'fa-regular fa-circle-x',
        },
      ],
    },
  },
  {
    title: 'Pro',
    description: 'Elevate your forms to reflect your brand’s identity',
    icon: 'fa-regular fa-cube',
    price: {
      monthly: 24,
      yearly: Math.ceil(216 / 12),
    },
    features: {
      card: [
        {
          id: 'ai-form-builder',
          title: 'Unlimited AI Chat Assistant',
          description: 'Build, design, and refine forms through a natural chat conversation — just describe what you want and the AI handles the rest',
          icon: 'fa-regular fa-wand-magic-sparkles',
        },
        {
          id: 'ai-support',
          title: 'AI Support & Guidance',
          description: 'Get instant answers, tips, and walkthroughs for any feature on the platform, available 24/7',
          icon: 'fa-regular fa-headset',
        },
        {
          id: 'branding',
          title: 'Remove Formsuite branding',
          description: 'Hide the "built with Formsuite" badge and all Formsuite branding',
          icon: 'fa-regular fa-pencil',
        },
        {
          id: 'ai-conversation',
          title: 'AI conversation blocks',
          description: 'Conversational blocks allow AI to ask and answer questions with respondents',
          icon: 'fa-regular fa-comment',
        },
        {
          id: 'partial-submissions',
          title: 'Partial submissions',
          description: 'Collect and save form responses before they are finished.',
          icon: 'fa-regular fa-table',
        },
        {
          id: 'ai-summary',
          title: 'Response summaries',
          description: 'Easliy understand your responses with AI summaries',
          icon: 'fa-regular fa-bolt',
        },
        {
          id: 'collab',
          title: 'Built for teams',
          description: 'Invite up to 5 team members to your workspace',
          icon: 'fa-regular fa-user',
        },
        {
          id: 'folders-themes',
          title: 'Folders & saved themes',
          description: 'Organize forms into up to 5 folders and reuse up to 5 saved brand themes across them',
          icon: 'fa-regular fa-folder',
        },
        {
          id: 'duplicate-forms',
          title: 'Duplicate forms',
          description: 'Make a one-click copy of any form to spin up variations or templates of your best performers',
          icon: 'fa-regular fa-copy',
        },
        {
          id: 'notification',
          title: 'Get notified of responses',
          description: 'Recieve email notifications when a form has engagement',
          icon: 'fa-regular fa-paper-plane',
        },
        {
          id: 'unlimited-uploads',
          title: 'Unlimited uploads',
          description: 'Remove the the 10MB limit for file uploads ',
          icon: 'fa-regular fa-upload',
        },
        {
          id: 'advanced-analytics',
          title: 'Advanced analytics',
          description: 'Get additional insights into your responses with advanced analytics',
          icon: 'fa-regular fa-chart-line',
        },
        {
          id: 'reports',
          title: 'Shareable reports',
          icon: 'fa-regular fa-file-chart-column',
          description: 'Create and share AI insights and detailed reports with your team using shared links',
        },
        {
          id: 'key-insights',
          title: 'AI key insights',
          icon: 'fa-regular fa-brain',
          description: 'Easily understand your audience with generated AI key insights',
        },
        {
          id: 'scheduler',
          title: 'Scheduler bookings',
          description:
            'Let respondents book without leaving your form. Integrates with Google Calendar, Cal.com, Calendly, and your existing workflows.',
          icon: 'fa-regular fa-calendar',
        },
      ],
      usage: [
        {
          id: 'number-of-forms',
          title: 'Number of forms',
          description: 'Unlimited',
        },
        {
          id: 'questions-per-form',
          title: 'Questions per form',
          description: 'Unlimited',
        },
        {
          id: 'form-responses',
          title: 'Form responses',
          description: 'Unlimited',
        },
        {
          id: 'workspace-members',
          title: 'Workspace members',
          description: '5',
        },
      ],
      core: [],
      branding: [],
      customizations: [
        {
          id: 'partial-submissions',
          title: 'Partial submissions',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'ai-form-builder',
          title: 'AI Chat Assistant',
          description: 'Unlimited',
        },
        {
          id: 'ai-support',
          title: 'AI Support & Guidance',
          description: 'Unlimited',
        },
        {
          id: 'ai-conversation',
          title: 'AI conversation blocks',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'upload-media',
          title: 'Upload your own media',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'notification-emails',
          title: 'Notification emails on form submissions',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'remove-branding',
          title: 'Remove built with Formsuite badge',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'branding-logo',
          title: 'Branding logo',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'duplicate-form',
          title: 'Duplicate forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-domains',
          title: 'Custom domains',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'advanced-ai-training',
          title: 'Advanced AI training',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'add-logic-to-forms',
          title: 'Add logic to forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'hidden-fields',
          title: 'Hidden fields',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'template-forms',
          title: 'Template forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-fonts',
          title: 'Custom fonts',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-themes',
          title: 'Custom themes',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'scheduler-bookings',
          title: 'Scheduler bookings',
          schedulerProviders: ['CALENDLY', 'CALCOM', 'GOOGLE_CALENDAR'],
        },
        {
          id: 'google-calendar-scheduling',
          title: 'Google Calendar & Meet links',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'revisit-incomplete',
          title: 'Revisit incomplete forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'multi-question-pages',
          title: 'Multi-question pages',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'webhooks',
          title: 'Webhooks',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'redirect-question',
          title: 'Redirect question',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'form-folders',
          title: 'Form folders',
          description: '5',
        },
        {
          id: 'custom-themes-count',
          title: 'Saved custom themes',
          description: '5',
        },
      ],
      insight: [
        {
          id: 'ai-lead-analysis',
          title: 'AI lead analysis',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'lead-summaries',
          title: 'Response summaries',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'reports',
          title: 'Reports',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'download-responses',
          title: 'Download responses',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'advanced-form-metrics',
          title: 'Advanced form metrics',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'workspace-metrics',
          title: 'Workspace metrics',
          icon: 'fa-regular fa-circle-check',
        },
      ],
      support: [],
      sharing: [
        {
          id: 'customized-end-screen',
          title: 'Customized end screen',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'link-to-url',
          title: 'Link to URL on end screen',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'embed-forms',
          title: 'Embed forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'popup-forms',
          title: 'Popup forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'banner-forms',
          title: 'Banner forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-domains-count',
          title: 'Custom domains',
          description: '1',
        },
      ],
    },
  },
  {
    title: 'Business',
    description: 'Unlock unlimited potential with advanced features and support',
    icon: 'fa-regular fa-cubes',
    price: {
      monthly: 69,
      yearly: Math.ceil(624 / 12),
    },
      features: {
        card: [
          {
            id: 'included-in-pro',
            title: 'Everything in Pro',
            description: 'Everything in Pro, plus additional tools for teams who need advanced features',
            icon: 'fa-regular fa-badge-check',
          },
          {
            id: 'custom-domains',
            title: 'Unlimited custom domains',
            description: 'Run as many client or brand domains as you need (forms.yourdomain.com). Pro includes one; Business is unlimited.',
            icon: 'fa-regular fa-globe',
          },
          {
            id: 'unlimited-folders',
            title: 'Unlimited folders',
            description: 'Organize your forms across as many folders as you need without hitting plan caps.',
            icon: 'fa-regular fa-folder',
          },
          {
            id: 'unlimited-themes',
            title: 'Unlimited saved themes',
            description: 'Save unlimited reusable themes so larger teams can keep every form on-brand.',
            icon: 'fa-regular fa-palette',
          },
          {
            id: 'priority-support',
            title: 'Priority support',
            description: 'We hear for every request and will do our best to implement it',
            icon: 'fa-regular fa-headset',
          },
        ],
      usage: [
        {
          id: 'number-of-forms',
          title: 'Number of forms',
          description: 'Unlimited',
        },
        {
          id: 'questions-per-form',
          title: 'Questions per form',
          description: 'Unlimited',
        },
        {
          id: 'form-responses',
          title: 'Form responses',
          description: 'Unlimited',
        },
        {
          id: 'workspace-members',
          title: 'Workspace members',
          description: 'Unlimited',
        },
      ],
      core: [],
      branding: [],
      customizations: [
        {
          id: 'partial-submissions',
          title: 'Partial submissions',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'ai-form-builder',
          title: 'AI Chat Assistant',
          description: 'Unlimited',
        },
        {
          id: 'ai-support',
          title: 'AI Support & Guidance',
          description: 'Unlimited',
        },
        {
          id: 'ai-conversation',
          title: 'AI conversation blocks',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'upload-media',
          title: 'Upload your own media',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'notification-emails',
          title: 'Notification emails on form submissions',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'remove-branding',
          title: 'Remove built with Formsuite badge',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'branding-logo',
          title: 'Branding logo',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'duplicate-form',
          title: 'Duplicate forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-domains',
          title: 'Custom domains',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'advanced-ai-training',
          title: 'Advanced AI training',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'add-logic-to-forms',
          title: 'Add logic to forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'hidden-fields',
          title: 'Hidden fields',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'template-forms',
          title: 'Template forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-fonts',
          title: 'Custom fonts',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-themes',
          title: 'Custom themes',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'scheduler-bookings',
          title: 'Scheduler bookings',
          schedulerProviders: ['CALENDLY', 'CALCOM', 'GOOGLE_CALENDAR'],
        },
        {
          id: 'google-calendar-scheduling',
          title: 'Google Calendar & Meet links',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'revisit-incomplete',
          title: 'Revisit incomplete forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'multi-question-pages',
          title: 'Multi-question pages',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'webhooks',
          title: 'Webhooks',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'redirect-question',
          title: 'Redirect question',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'form-folders',
          title: 'Form folders',
          description: 'Unlimited',
        },
        {
          id: 'custom-themes-count',
          title: 'Saved custom themes',
          description: 'Unlimited',
        },
      ],
      insight: [
        {
          id: 'ai-lead-analysis',
          title: 'AI lead analysis',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'lead-summaries',
          title: 'Response summaries',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'reports',
          title: 'Reports',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'download-responses',
          title: 'Download responses',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'advanced-form-metrics',
          title: 'Advanced form metrics',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'workspace-metrics',
          title: 'Workspace metrics',
          icon: 'fa-regular fa-circle-check',
        },
      ],
      support: [],
      sharing: [
        {
          id: 'customized-end-screen',
          title: 'Customized end screen',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'link-to-url',
          title: 'Link to URL on end screen',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'embed-forms',
          title: 'Embed forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'popup-forms',
          title: 'Popup forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'banner-forms',
          title: 'Banner forms',
          icon: 'fa-regular fa-circle-check',
        },
        {
          id: 'custom-domains-count',
          title: 'Custom domains',
          description: 'Unlimited',
        },
      ],
    },
  },
];